<template>
  <section class="benefits-points-section">
    <header class="benefits-points-header">
      <label class="benefits-points-toggle-row">
        <span>{{ t("benefitsCenter-points-systemLabel") }}</span>

        <BenefitsToggle
          :key="toggleKey"
          :value="internalValue.enabled"
          :aria-label="t('benefitsCenter-points-systemLabel')"
          @input="onTogglePoints"
        />
      </label>

      <div v-if="internalValue.enabled" class="benefits-points-settings">
        <div class="benefits-field">
          <label class="benefits-label" :for="pointsFactorId">{{ pointsFactorLabel }}</label>

          <KvInput
            :id="pointsFactorId"
            v-model.number="internalValue.pointsFactor"
            type="number"
            :min="1"
            variant="outlined"
            size="large"
            @change="persistSection"
          />

          <p v-if="accrualPreview" class="benefits-points-preview">{{ accrualPreview }}</p>
        </div>

        <div class="benefits-field">
          <label class="benefits-label" :for="maxPointsPerOrderId">
            {{ t("benefitsCenter-points-maxPointsPerOrderLabel") }}
          </label>

          <KvInput
            :id="maxPointsPerOrderId"
            v-model="internalValue.maxPointsPerOrder"
            type="number"
            :min="1"
            variant="outlined"
            size="large"
            :placeholder="t('benefitsCenter-points-maxPointsPerOrderPlaceholder')"
            @change="persistSection"
          />
        </div>
      </div>

      <hr aria-hidden="true" />

      <h2>{{ t("benefitsCenter-points-title") }}</h2>
    </header>

    <div v-if="internalValue.items.length" class="benefits-points-list">
      <KvButton class="benefits-points-add-button" type="button" variant="outlined" prepend-icon="ui-plus"
        @click="openCreatePanel">
        {{ t("benefitsCenter-points-addReward") }}
      </KvButton>

      <BenefitsRewardCard
        v-for="reward in internalValue.items"
        :key="reward.id"
        :reward="reward"
        @click="openEditPanel"
        @save-reward="saveRewardStatus"
      />
    </div>

    <BenefitsEmptyState v-else :title="t('benefitsCenter-points-emptyTitle')"
      :description="t('benefitsCenter-points-emptyDescription')" :button-text="t('benefitsCenter-points-createFirst')"
      @action="openCreatePanel">
      <template #background>
        <article v-for="i in 3" :key="i" class="benefits-points-example-card"
          :style="{ opacity: i === 1 ? 1 : i === 2 ? 0.5 : 0.25 }">
          <div class="benefits-points-example-image"
            style="background-image: url(https://kevi-uploads.nyc3.digitaloceanspaces.com/kevi//image%208_275088178638000001.svg); background-size: cover; background-position: center;">
          </div>

          <div class="benefits-points-example-content">
            <div class="benefits-points-example-header">
              <div class="benefits-points-example-cost">{{ t("benefitsCenter-points-exampleCost") }}</div>
            </div>

            <div class="benefits-points-example-info">
              <strong>{{ t("benefitsCenter-points-exampleName") }}</strong>
              <span>{{ t("benefitsCenter-points-exampleSubtitle") }}</span>
            </div>
          </div>
        </article>
      </template>
    </BenefitsEmptyState>

    <BenefitsPointRewardPanel v-model="panelOpen" :reward="selectedReward" :rewards="internalValue.items"
      :tiers="tiers" :tiers-enabled="tiersEnabled" @save="saveReward" @delete="deleteReward" @close="selectedReward = null" />

    <confirm
      id="confirmPointsDeactivate"
      ref="confirmDeactivate"
      button-class="btn-danger"
      @confirm="confirmDeactivation"
    >
      <template slot="title">
        {{ t("benefitsCenter-points-deactivateModalTitle") }}
      </template>
      <template slot="body">
        <template v-if="deactivationImpact.length > 0">
          <p class="benefits-points-modal-desc">{{ t("benefitsCenter-points-deactivateModalDescription") }}</p>
          <ul class="benefits-points-impact-list">
            <li v-for="auto in deactivationImpact" :key="auto.id">
              <strong>{{ auto.title }}</strong>
            </li>
          </ul>
        </template>
        <template v-else>
          <p class="benefits-points-modal-desc">{{ t("benefitsCenter-points-deactivateModalEmptyDescription") }}</p>
        </template>

        <div class="benefits-points-impact-general">
          <p class="benefits-points-impact-general__title">
            {{ t("benefitsCenter-points-deactivateModalImpactTitle") }}
          </p>
          <ul class="benefits-points-impact-general__list">
            <li>{{ t("benefitsCenter-points-deactivateModalImpactSales") }}</li>
            <li>{{ t("benefitsCenter-points-deactivateModalImpactSegments") }}</li>
            <li>{{ t("benefitsCenter-points-deactivateModalImpactRewards") }}</li>
          </ul>
        </div>
      </template>
      <template slot="confirm">
        {{ t("benefitsCenter-points-deactivateModalConfirm") }}
      </template>
    </confirm>
  </section>
</template>

<script>
import keviApi from "../../../keviApi";
import BenefitsPointRewardPanel from "./benefits-point-reward-panel.vue";
import BenefitsEmptyState from "../../shared/benefits-empty-state.vue";
import BenefitsRewardCard from "./benefits-reward-card.vue";
import BenefitsToggle from "../../shared/benefits-toggle.vue";
import KvInput from "../../../components/inputs/kv-input.vue";
import Icon from "../../../components/icon/icon";
import currencyMixin from "../../../mixins/currency-mixin";

const PREVIEW_AMOUNT = 100;

export default {
  name: "BenefitsPointsSection",
  mixins: [currencyMixin],
  components: {
    BenefitsPointRewardPanel,
    BenefitsEmptyState,
    BenefitsRewardCard,
    BenefitsToggle,
    KvInput,
    Icon,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    tiers: {
      type: Array,
      default: () => [],
    },
    tiersEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalValue: this.normalizeValue(this.value),
      panelOpen: false,
      selectedReward: null,
      pointsFactorId: "benefits-points-factor",
      maxPointsPerOrderId: "benefits-points-max-per-order",
      toggleKey: 0,
      deactivationImpact: [],
      loadingImpact: false,
    };
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.internalValue = this.normalizeValue(value);
      },
    },
  },
  computed: {
    pointsFactorLabel() {
      return this.t("benefitsCenter-points-pointsFactorLabel", { currency: this.currencySymbol });
    },
    accrualPreview() {
      const factor = Number(this.internalValue.pointsFactor);

      if (!factor || factor < 1) {
        return "";
      }

      const uncapped = Math.floor(PREVIEW_AMOUNT / factor);
      const cap = String(this.internalValue.maxPointsPerOrder ?? "").trim();
      const capped = cap === "" ? uncapped : Math.min(uncapped, Number(cap));
      const term = capped < uncapped
        ? "benefitsCenter-points-accrualPreviewCapped"
        : "benefitsCenter-points-accrualPreview";

      return this.t(term, {
        amount: this.formatCurrency(PREVIEW_AMOUNT),
        points: capped,
      });
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    normalizeValue(value) {
      return {
        enabled: value?.enabled === true,
        pointsFactor: value?.pointsFactor ?? 1,
        maxPointsPerOrder: value?.maxPointsPerOrder ?? "",
        items: Array.isArray(value?.items) ? value.items : [],
      };
    },
    emitChange() {
      this.$emit("input", {
        ...this.internalValue,
        items: [...this.internalValue.items],
      });
    },
    persistSection() {
      this.emitChange();
      this.saveSection();
    },
    onTogglePoints(newVal) {
      if (newVal) {
        this.internalValue.enabled = true;
        this.persistSection();
      } else {
        this.toggleKey++;
        this.checkDeactivationImpact();
      }
    },
    checkDeactivationImpact() {
      if (this.loadingImpact) return;
      this.loadingImpact = true;

      this.$http(keviApi.getOptions("benefits/program/points-deactivation-impact"))
        .then((response) => {
          this.deactivationImpact = response.body?.affected_automations ?? [];
          this.$refs.confirmDeactivate.open();
        })
        .catch(() => {
          this.deactivationImpact = [];
          this.$refs.confirmDeactivate.open();
        })
        .finally(() => {
          this.loadingImpact = false;
        });
    },
    confirmDeactivation() {
      this.internalValue.enabled = false;
      this.toggleKey++;
      this.persistSection();
    },
    saveRewardStatus(reward) {
      this.$emit("save-reward", reward);
    },
    saveSection() {
      const maxPointsPerOrder = String(this.internalValue.maxPointsPerOrder ?? "").trim();

      this.$emit("save-section", "points", {
        enabled: this.internalValue.enabled,
        pointsFactor: Number(this.internalValue.pointsFactor) || 1,
        maxPointsPerOrder: Number(maxPointsPerOrder) >= 1 ? Number(maxPointsPerOrder) : null,
        items: this.internalValue.items,
      });
    },
    openCreatePanel() {
      this.selectedReward = null;
      this.panelOpen = true;
    },
    openEditPanel(reward) {
      this.selectedReward = reward;
      this.panelOpen = true;
    },
    saveReward(reward) {
      this.$emit("save-reward", reward);
    },
    deleteReward(reward) {
      this.$emit("delete-reward", reward);
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-points-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  ::v-deep .kv-input {
    width: 100%;
  }
}

.benefits-points-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  h2 {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0;
  }

  hr {
    border: 0;
    border-top: 1px solid #e5e7eb;
    margin: 0;
    width: 100%;
  }
}

.benefits-points-settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
  width: 100%;
}

.benefits-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.benefits-label {
  color: #384250;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  margin: 0;
}

.benefits-points-preview {
  color: #6c737f;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 1.3;
  margin: 0;
}

@media (max-width: 48rem) {
  .benefits-field {
    width: 100%;
  }
}

.benefits-points-toggle-row {
  align-items: center;
  align-self: flex-start;
  color: #384250;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 8px;
  line-height: 1.2;
  margin: 0;

  &.small {
    margin-left: auto;
  }
}

.benefits-points-toggle-control {
  display: inline-flex;
}

.benefits-points-toggle-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.benefits-points-toggle {
  background: #ffffff;
  border: 0.5px solid #f3f4f6;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  height: 18px;
  padding: 1px 4px;
  position: relative;
  width: 40px;

  &::before {
    background: #d2d6db;
    border: 0.5px solid #9da4ae;
    border-radius: 3px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    content: "";
    height: 14px;
    left: 2px;
    position: absolute;
    top: 1px;
    transition: transform 0.15s ease;
    width: 16px;
  }
}

.benefits-points-toggle-input:checked+.benefits-points-toggle {
  background: #d3f8e0;
  border-color: #17b26a;

  &::before {
    background: #17b26a;
    border-color: #097347;
    transform: translateX(18px);
  }
}

.benefits-points-toggle-input:focus-visible+.benefits-points-toggle {
  outline: 2px solid rgba(42, 170, 76, 0.35);
  outline-offset: 2px;
}

.benefits-points-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.benefits-points-add-button {
  width: 100%;
  min-height: 56px;
  font-size: 16px;
  font-weight: 400;
}

.benefits-points-example-card {
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #2aaa4c;
  border-radius: 16px;
  display: flex;
  gap: 16px;
  padding: 12px;
  position: relative;
  width: 100%;
}

.benefits-points-example-image {
  background: #f3f4f6;
  border-radius: 12px;
  flex: 0 0 120px;
  height: 120px;
  width: 120px;
}

.benefits-points-example-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-between;
  padding: 4px 0;
}

.benefits-points-example-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.benefits-points-example-cost {
  align-items: center;
  background: #d3f8e0;
  border-radius: 8px;
  color: #17b26a;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 12px;
  width: fit-content;
}

.benefits-points-example-info {
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
  }

  span {
    color: #4d5761;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
  }
}

.benefits-points-modal-desc {
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 8px;
}

.benefits-points-impact-list {
  list-style-type: disc;
  margin: 6px 0 14px 20px;
  padding: 0;
  max-height: 140px;
  overflow-y: auto;

  li {
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 3px;

    strong {
      color: #1f2937;
    }
  }
}

.benefits-points-impact-general {
  margin-top: 14px;
  padding: 10px 14px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }

  &__list {
    list-style-type: disc;
    margin: 0 0 0 16px;
    padding: 0;

    li {
      font-size: 13px;
      color: #6b7280;
      line-height: 1.5;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

</style>
